/**
 * Unified lookup data — prefers GET /bootstrap/lookups, falls back to domain APIs.
 * Avoids duplicate localStorage caches and parallel fetches for the same reference data.
 */
import { apiService } from './apiService';
import { fetchBootstrapLookups } from './bootstrapService';
import type { BootstrapRole, BootstrapLocation } from './bootstrapService';
import type { Category, Vendor } from './masterDataService';
import type { Role, Location as StockLocation } from '../types';

function toAppRole(r: BootstrapRole): Role {
  return {
    id: r.id,
    name: r.name,
    description: r.description || '',
    permissions: [],
    created_at: '',
    updated_at: '',
  };
}

function toAppLocation(loc: BootstrapLocation | StockLocation): StockLocation {
  return {
    id: loc.id,
    name: loc.name,
    code: loc.code ?? '',
    address: loc.address,
    phone: 'phone' in loc ? loc.phone : undefined,
    email: 'email' in loc ? loc.email : undefined,
    manager_id: 'manager_id' in loc ? loc.manager_id : undefined,
    is_warehouse: loc.is_warehouse ?? false,
    is_active: loc.is_active,
    created_at: 'created_at' in loc ? (loc.created_at ?? '') : '',
    updated_at: 'updated_at' in loc ? (loc.updated_at ?? '') : '',
  };
}

export interface LocationLookupParams {
  skip?: number;
  limit?: number;
  active_only?: boolean;
  is_active?: boolean;
  search?: string;
  is_warehouse?: boolean;
  location_type?: string;
  forceRefresh?: boolean;
}

function canUseBootstrapLocations(params?: LocationLookupParams): boolean {
  if (params?.forceRefresh) return false;
  if (params?.search) return false;
  if (params?.is_warehouse !== undefined) return false;
  if (params?.location_type) return false;
  if (params?.is_active === false) return false;
  if (params?.active_only === false) return false;
  if (params?.skip !== undefined && params.skip > 0) return false;
  return true;
}

function canUseBootstrapList(params?: { skip?: number; limit?: number; active_only?: boolean; search?: string; forceRefresh?: boolean }): boolean {
  if (params?.forceRefresh) return false;
  if (params?.search) return false;
  if (params?.active_only === false) return false;
  if (params?.skip !== undefined && params.skip > 0) return false;
  return true;
}

export const lookupService = {
  async getCategories(params?: {
    skip?: number;
    limit?: number;
    active_only?: boolean;
    forceRefresh?: boolean;
  }): Promise<Category[]> {
    if (canUseBootstrapList(params)) {
      try {
        const boot = await fetchBootstrapLookups(params?.forceRefresh);
        if (boot.categories.length) {
          const limit = params?.limit ?? boot.categories.length;
          return boot.categories.slice(0, limit) as Category[];
        }
      } catch {
        /* API fallback */
      }
    }
    return apiService.get<Category[]>('/products/categories/', params);
  },

  async getVendors(params?: {
    skip?: number;
    limit?: number;
    active_only?: boolean;
    search?: string;
    forceRefresh?: boolean;
  }): Promise<Vendor[]> {
    if (canUseBootstrapList(params)) {
      try {
        const boot = await fetchBootstrapLookups(params?.forceRefresh);
        if (boot.vendors.length) {
          const limit = params?.limit ?? boot.vendors.length;
          return boot.vendors.slice(0, limit) as Vendor[];
        }
      } catch {
        /* API fallback */
      }
    }
    return apiService.get<Vendor[]>('/vendors/', params);
  },

  async getLocations(params?: LocationLookupParams): Promise<StockLocation[]> {
    if (canUseBootstrapLocations(params)) {
      try {
        const boot = await fetchBootstrapLookups(params?.forceRefresh);
        if (boot.locations.length) {
          const limit = params?.limit ?? boot.locations.length;
          return boot.locations.slice(0, limit).map((loc: BootstrapLocation) => toAppLocation(loc));
        }
      } catch {
        /* API fallback */
      }
    }
    const { forceRefresh: _fr, ...apiParams } = params || {};
    const rows = await apiService.get<StockLocation[]>('/locations/', apiParams);
    return rows.map((loc: StockLocation) => toAppLocation(loc));
  },

  /** Roles for admin user-management dropdowns (bootstrap when available). */
  async getRoles(params?: {
    skip?: number;
    limit?: number;
    forceRefresh?: boolean;
  }): Promise<Role[]> {
    if (!params?.skip && !params?.forceRefresh) {
      try {
        const boot = await fetchBootstrapLookups(params?.forceRefresh);
        if (boot.roles.length) {
          return boot.roles.map(toAppRole);
        }
      } catch {
        /* API fallback */
      }
    }
    return apiService.get<Role[]>('/admin/roles/', params);
  },
};

export default lookupService;
